import type { IUseAccess } from '@hooks/useAccess';
import { useCloseContextMenuOnScroll } from '@hooks/useCloseContextMenuOnScroll';
import { Button, ContextMenu, IconKebab } from '@uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { RatioIdType } from '../../../types';

type TableRowActionsButtonProps = {
    id: RatioIdType;
    onEdit: (id: RatioIdType) => () => void;
    onRemove: (id: Exclude<RatioIdType, undefined>) => () => void;
    catalogRatiosRules: IUseAccess['catalogRatios'];
    disabled?: boolean;
};

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');

export const TableRowActionsButton = ({
    id,
    onEdit,
    onRemove,
    catalogRatiosRules,
    disabled,
}: TableRowActionsButtonProps) => {
    const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);

    useCloseContextMenuOnScroll({ setOpen: setIsContextMenuOpen });

    const rowActionsButtonRef = useRef(null);

    const handleRowActionsButtonClick = useCallback(() => {
        setIsContextMenuOpen((prevState) => !prevState);
    }, [setIsContextMenuOpen]);

    const handleContextMenuItemClick = useCallback((): any => {
        handleRowActionsButtonClick();
    }, [handleRowActionsButtonClick]);

    const contextItems = useMemo(() => {
        const items = [];

        if (catalogRatiosRules.canEdit) {
            items.push({
                label: 'Редактировать',
                action: () => {
                    onEdit(id)();
                    handleContextMenuItemClick();
                },
            });
        }
        if (id && catalogRatiosRules.canDelete) {
            items.push({
                label: 'Удалить',
                action: () => {
                    onRemove(id)();
                    handleContextMenuItemClick();
                },
            });
        }

        return items;
    }, [catalogRatiosRules.canDelete, catalogRatiosRules.canEdit, handleContextMenuItemClick, id, onEdit, onRemove]);

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                onClick={handleRowActionsButtonClick}
                ref={rowActionsButtonRef}
                disabled={disabled}
            />
            <ContextMenu
                isOpen={isContextMenuOpen}
                items={contextItems}
                getItemLabel={getItemLabel}
                anchorRef={rowActionsButtonRef}
                direction="downStartLeft"
                getItemOnClick={getItemOnClick}
                onClickOutside={handleRowActionsButtonClick}
            />
        </>
    );
};

TableRowActionsButton.displayName = 'TableRowActionsButton';
